/**
 * "Ask people who own <noun>" — the question ask on a category page (slice SG-1 of the category
 * questions plan, 2026-10-17). Pure and client-safe: the copy, the rules the composer follows,
 * the stored draft, the create body and the query validators the two read proxies share
 * (`pages/api/owner-notes/discussions/{reach,answered}.ts`).
 *
 * The CTA is in the category page's edge-cached HTML, so its copy is static and reader-neutral
 * (the review gate's rule, 95713c9a): it names no method, no number, nobody. Everything that
 * depends on the reader or on DealHunt (the reach, the answered questions, the posted line)
 * renders only once the reader opened the composer.
 *
 * Reader copy says "SoundGuys team", never "staff" (ab20bd07), and nothing sells a purchase
 * (review voice).
 */
import {
  DISCUSSION_LIMITS,
  TITLE_MIN_TO_POST,
  cleanTitle,
  toGearInput,
  type DraftGear,
} from 'lib/community/discussions-writer'
import type { DiscussionCard, DiscussionCreateBody } from 'lib/community/discussions-types'
import { AUDIO_NODE_SLUG } from 'lib/dealhunt/constants'

// ── copy ─────────────────────────────────────────────────────────────────────

/** Below this many owners the reach is said without a figure ("the few owners here"). */
export const REACH_FIGURE_MIN = 5

function ownersOf(n: number, noun: string): string {
  return n === 1 ? `1 owner of ${noun}` : `${n.toLocaleString('en-US')} owners of ${noun}`
}

export const QUESTION_COPY = {
  /** The composer form's accessible name. (The undeclared strip's own ask — "Ask owners a
   *  question →" / "Ask owners" — went 2026-10-27; only the declared strip asks.) */
  formLabel: (noun: string): string => `Ask people who own ${noun}`,
  /** The declared strip's one line under the buy-again row. */
  declaredLead: (noun: string): string => `Something to ask other ${noun} owners?`,
  declaredLink: 'Ask a question →',
  /** The writer's own placeholders (`kindCopy('question')`, `TextBlock`). */
  titlePlaceholder: 'What do you want to know?',
  titleLabel: 'Your question',
  detailPlaceholder: "Say what you've tried, what you use it for, what matters to you.",
  detailLabel: 'Details (optional)',
  productLabel: 'Link a product (optional)',
  productPlaceholder: (noun: string): string => `Search ${noun}…`,
  productRemove: (name: string): string => `Remove ${name}`,
  answeredLabel: 'Answered here',
  openersLead: (noun: string): string =>
    `Nobody has asked ${noun} owners here yet: you'd be first.`,
  fine: "Sign in at Post. Your draft stays in this tab. You'll get a bell and an email when someone answers.",
  post: 'Post question',
  posting: 'Posting…',
  cancel: 'Cancel',
  seeThread: 'See your question in the community →',
  teamAsked: 'Team asked',
  postFailed: 'Something went wrong — try again in a moment.',
  titleTooShort: `Write a title of at least ${TITLE_MIN_TO_POST} characters to post.`,
} as const

/** The sign-in dialog when Post opens it (`showLogIn({ copy })`). No speed, no moderator
 *  (aca4da79, 6496d8bc); the dialog lists the methods itself. */
export const QUESTION_LOGIN_DIALOG = {
  title: 'Sign in to post your question',
  subtitle: "Your question is kept. Once you're in, it posts.",
} as const

/**
 * The composer's header line, from the reach read: a figure from `REACH_FIGURE_MIN` owners, "the
 * few owners here" under it. null = no reach answer (DealHunt without the route, a failed read):
 * the line is left out, the ask still posts.
 */
export function reachLine(owners: number | null, noun: string): string | null {
  if (owners === null) {
    return null
  }
  const who =
    owners >= REACH_FIGURE_MIN
      ? `Goes to about ${ownersOf(owners, noun)} and the SoundGuys team.`
      : 'Goes to the SoundGuys team and the few owners here.'
  return `${who} Owners' answers are pinned first.`
}

/**
 * The posted line. `notified` = DealHunt's `owners_notified` on the created post (exact, written
 * in the post's transaction); without it the reach read, said "about"; without either, the team.
 */
export function postedLine(notified: number | null, reach: number | null, noun: string): string {
  if (notified !== null && notified > 0) {
    const has = notified === 1 ? 'has' : 'have'
    return `Posted. ${ownersOf(notified, noun)} and the SoundGuys team ${has} been told.`
  }
  if (notified === null && reach !== null && reach >= REACH_FIGURE_MIN) {
    return `Posted. About ${ownersOf(reach, noun)} and the SoundGuys team have been told.`
  }
  if (notified === null && reach !== null && reach > 0) {
    return 'Posted. The SoundGuys team and the few owners here have been told.'
  }
  return 'Posted. The SoundGuys team has been told.'
}

/** A count DealHunt sent: a finite, non-negative integer; anything else is null. */
export function countOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : null
}

/**
 * Three openers for a category nobody has asked in yet — questions an owner can answer, templated
 * on the noun. Each fills the title on a tap (never over the reader's own words). Not the
 * Discussions tab's `DISCUSSION_OPENERS`: those are generic community prompts, several are not
 * questions, and none is about a category.
 */
export function categoryOpeners(noun: string): string[] {
  return [
    `How have your ${noun} held up after a year or more?`,
    `What do you wish you'd known before choosing your ${noun}?`,
    `What would you look for in ${noun} if you started again?`,
  ]
}

// ── the slot under the title ────────────────────────────────────────────────

/** From this many words in the title the slot searches the answered questions (`q=`). */
export const SLOT_QUERY_WORDS = 3
/** Typing pause before the typed search goes out. */
export const SLOT_DEBOUNCE_MS = 350
/** At most this many questions in the slot. */
export const SLOT_LIMIT = 3

export function wordCount(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length
}

/** Only an answered QUESTION ever reaches the slot, whatever DealHunt sent. */
export function answeredOnly(
  items: readonly DiscussionCard[],
  max: number = SLOT_LIMIT
): DiscussionCard[] {
  return items
    .filter(
      d =>
        d &&
        d.kind === 'question' &&
        typeof d.id === 'string' &&
        typeof d.title === 'string' &&
        (countOrNull(d.answer_count) ?? 0) > 0
    )
    .slice(0, max)
}

export type QuestionSlot =
  | { mode: 'none' }
  | { mode: 'answered'; items: DiscussionCard[] }
  | { mode: 'openers'; openers: string[] }

/**
 * What the slot shows.
 * - Title of `SLOT_QUERY_WORDS`+ words: the answered questions matching it (`typed`); nothing
 *   while that read is out, failed, or found none.
 * - Before that: the category's answered questions (`latest`); the openers ONLY when that read
 *   answered with none — a failed read (null) shows nothing, never "nobody has asked" (84465965).
 */
export function questionSlot(
  title: string,
  latest: readonly DiscussionCard[] | null,
  typed: readonly DiscussionCard[] | null,
  noun: string
): QuestionSlot {
  if (wordCount(title) >= SLOT_QUERY_WORDS) {
    const items = typed ? answeredOnly(typed) : []
    return items.length ? { mode: 'answered', items } : { mode: 'none' }
  }
  if (latest === null) {
    return { mode: 'none' }
  }
  const items = answeredOnly(latest)
  if (items.length) {
    return { mode: 'answered', items }
  }
  return { mode: 'openers', openers: categoryOpeners(noun) }
}

// ── the post ────────────────────────────────────────────────────────────────

export interface QuestionDraft {
  title: string
  detail: string
  /** One linked product (a `gear` item), or none. */
  gear: Extract<DraftGear, { kind: 'product' }> | null
}

export const EMPTY_QUESTION_DRAFT: QuestionDraft = { title: '', detail: '', gear: null }

/** Why Post cannot send yet (shown once the title has words), or null. The detail is optional: a
 *  question needs no text (`postBlocker` asks text of a discussion only). */
export function questionBlocker(draft: QuestionDraft): string | null {
  return draft.title.trim().length < TITLE_MIN_TO_POST ? QUESTION_COPY.titleTooShort : null
}

/** `POST /discussions` as the writer sends it: a question on this category, the detail as one
 *  text block (none when empty), the chip as the one `gear` item. */
export function questionCreateBody(
  draft: QuestionDraft,
  categoryNodeId: string
): DiscussionCreateBody {
  const detail = draft.detail.trim().slice(0, DISCUSSION_LIMITS.textChars)
  return {
    kind: 'question',
    title: cleanTitle(draft.title).trim(),
    category_node_id: categoryNodeId,
    blocks: detail ? [{ type: 'text', body: detail }] : [],
    gear: draft.gear ? [toGearInput(draft.gear)] : [],
  }
}

// ── the per-tab draft ───────────────────────────────────────────────────────

/** One draft per category node, per tab (`sessionStorage`), like the review band's. */
export function questionDraftKey(categoryNodeId: string): string {
  return `sg:category-question-draft:${categoryNodeId}`
}

function sessionStore(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage
  } catch {
    return null
  }
}

function productGear(raw: unknown): QuestionDraft['gear'] {
  const g = raw as Partial<Extract<DraftGear, { kind: 'product' }>> | null
  if (!g || g.kind !== 'product' || typeof g.node_id !== 'string' || typeof g.name !== 'string') {
    return null
  }
  return {
    kind: 'product',
    node_id: g.node_id,
    name: g.name,
    sub: typeof g.sub === 'string' ? g.sub : '',
    image_url: typeof g.image_url === 'string' ? g.image_url : null,
    full_slug: typeof g.full_slug === 'string' ? g.full_slug : null,
  }
}

/** The stored draft, or null (none, unreadable, blank). Read after mount only. */
export function readQuestionDraft(categoryNodeId: string): QuestionDraft | null {
  try {
    const raw = sessionStore()?.getItem(questionDraftKey(categoryNodeId))
    if (!raw) {
      return null
    }
    const value = JSON.parse(raw) as Partial<QuestionDraft> | null
    const draft: QuestionDraft = {
      title: typeof value?.title === 'string' ? cleanTitle(value.title) : '',
      detail: typeof value?.detail === 'string' ? value.detail : '',
      gear: productGear(value?.gear),
    }
    return draft.title.trim() || draft.detail.trim() || draft.gear ? draft : null
  } catch {
    return null
  }
}

/** Stores the draft (a blank one removes it). Never throws. */
export function writeQuestionDraft(categoryNodeId: string, draft: QuestionDraft): void {
  try {
    const store = sessionStore()
    if (!store) {
      return
    }
    if (!draft.title.trim() && !draft.detail.trim() && !draft.gear) {
      store.removeItem(questionDraftKey(categoryNodeId))
      return
    }
    store.setItem(questionDraftKey(categoryNodeId), JSON.stringify(draft))
  } catch {
    // Storage full or blocked: the draft lives in the composer only.
  }
}

export function clearQuestionDraft(categoryNodeId: string): void {
  try {
    sessionStore()?.removeItem(questionDraftKey(categoryNodeId))
  } catch {
    // nothing stored
  }
}

// ── which pages ask, and the read proxies' params ───────────────────────────

/**
 * The category page can ask: DealHunt files a question only under a community category
 * (`communityCategories()`: an audio `category` / `subcategory` below the audio root), else it
 * refuses the post with `category_unknown`. A page that fails this renders no CTA.
 */
export function asksQuestions(node: { fullSlug: string; nodeType: string }): boolean {
  return (
    node.fullSlug.startsWith(`${AUDIO_NODE_SLUG}/`) &&
    (node.nodeType === 'category' || node.nodeType === 'subcategory')
  )
}

/** DealHunt's `NODE_ID_RE` (discussions `blocks.ts`). */
const NODE_ID_RE = /^[0-9a-zA-Z-]{1,64}$/

/** A taxonomy node id as a query value, or null. A repeated param is refused, not merged. */
export function nodeIdParam(raw: unknown): string | null {
  return typeof raw === 'string' && NODE_ID_RE.test(raw) ? raw : null
}

/** `q=`: one line, whitespace collapsed, clamped to the title limit; null when empty or not a
 *  single string. */
export function answeredQueryParam(raw: unknown): string | null {
  if (typeof raw !== 'string') {
    return null
  }
  const q = raw.replace(/\s+/g, ' ').trim().slice(0, DISCUSSION_LIMITS.titleChars).trim()
  return q || null
}

/** `limit=` clamped to 1..5; `SLOT_LIMIT` when absent or unreadable. */
export function answeredLimitParam(raw: unknown): number {
  const n = typeof raw === 'string' && /^\d{1,3}$/.test(raw) ? Number(raw) : SLOT_LIMIT
  return Math.min(5, Math.max(1, n))
}

// ── My gear's launch notice ─────────────────────────────────────────────────

/** The notice's dismiss mark (`localStorage`: once dismissed, on this browser for good). */
export const GEAR_QUESTIONS_NOTICE_KEY = 'sg:gear-questions-notice-dismissed'

/** A category name as it reads mid-sentence: "Earbuds" → "earbuds", "AV Receivers" → "AV
 *  receivers" (a word with two capitals keeps them). */
export function categoryInSentence(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .map(w => (/[A-Z].*[A-Z]/.test(w) ? w : w.toLowerCase()))
    .join(' ')
}

/** At most this many categories are named; past it, "your gear". */
const NOTICE_MAX_CATEGORIES = 3

/**
 * "Readers can now ask owners of earbuds and headphones questions. You'll get a bell when one
 * comes in." — the categories of the reader's reviewed gear (`shelf[].category`; declarations
 * carry none), deduped in order; none known, or too many: "ask you questions about your gear".
 */
export function gearQuestionsNoticeLine(
  categoryNames: readonly (string | null | undefined)[]
): string {
  const names: string[] = []
  for (const raw of categoryNames) {
    const name = typeof raw === 'string' ? categoryInSentence(raw) : ''
    if (name && !names.includes(name)) {
      names.push(name)
    }
  }
  const bell = "You'll get a bell when one comes in."
  if (!names.length || names.length > NOTICE_MAX_CATEGORIES) {
    return `Readers can now ask you questions about your gear. ${bell}`
  }
  const who =
    names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
  return `Readers can now ask owners of ${who} questions. ${bell}`
}

export const GEAR_QUESTIONS_NOTICE = {
  settings: 'Settings',
  dismiss: 'Dismiss',
} as const
